import * as React from 'react';
import { Autocomplete, TextField, ThemeProvider } from '@mui/material';
import { createTheme, useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import defaultTheme from '../Themes/defaultTheme';

export const AppBarSearch = React.memo(({ canvasRef, elements, filterOptions = {} }) => {
    const theme = useTheme();

    const handleSearch = React.useCallback((labelOptionValue) => {
        if (!labelOptionValue) return;
        canvasRef.current?.SelectItemOnCanvas(labelOptionValue.data.id);
        canvasRef.current?.FitToSelected();
    }, [canvasRef]);

    const themeOverrides = React.useMemo(() => createTheme(defaultTheme, {
        components: {
            MuiAutocomplete: {
                styleOverrides: {
                    popupIndicator: {
                        color: theme.palette.menuIcon.main,
                        '&:hover': {
                            color: theme.palette.menuIcon.main,
                        }
                    },
                    option: {
                        color: theme.palette.common.black,
                    },
                    clearIndicator: {
                        color: theme.palette.menuIcon.main,
                        '&:hover': {
                            color: theme.palette.menuIcon.main,
                        }
                    }
                }
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: theme.palette.menuIcon.main,
                        '&:hover': {
                            color: theme.palette.menuIcon.main,
                        },
                        "&.Mui-focused": {
                            color: theme.palette.menuIcon.main,
                        },
                    },
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        "&.Mui-focused": {
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderWidth: "1px",
                                borderColor: theme.palette.menuIcon.main,
                            },
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderRadius: 25,
                            borderColor: theme.palette.menuIcon.main
                        }
                    },
                    notchedOutline: {
                        borderColor: theme.palette.menuIcon.main,
                        borderRadius: 25,
                    },
                    input: {
                        color: theme.palette.menuIcon.main,
                        '::placeholder': {
                            color: theme.palette.menuIcon.main,
                            opacity: 1
                        }
                    }
                }
            }
        }
    }), [theme.palette.menuIcon.main, theme.palette.common.black]);

    const renderOption = React.useCallback((props, option) => (
        <Box
            component="li"
            sx={{ "& > span": { mr: 2, flexShrink: 0} }}
            {...props}
            key={option.data.id}
        >
            {option.data.name}
        </Box>
    ), []);

    const renderInput = React.useCallback((params) => (
        <TextField 
            {...params} 
            variant="outlined" 
            size='small' 
            label="Search" 
            InputProps={{
                ...params.InputProps,
                startAdornment: params.InputProps.startAdornment
            }} 
        />
    ), []);

    // Insert sorted, memoized options list (case-insensitive by name)
    const sortedElements = React.useMemo(() => {
        if (!Array.isArray(elements)) return [];
        
        // Filter elements based on filterOptions
        const filteredElements = elements.filter(element => {
            // Default filters
            const hasValidName = element?.data?.name && element.data.name.trim() !== "";
            const isNode = element?.group === "nodes";
            
            // Apply custom filters from filterOptions
            const passesCustomFilters = !filterOptions.excludeGroups || 
                !filterOptions.excludeGroups.includes(element?.group);
            
            const passesNameFilter = !filterOptions.namePattern || 
                element?.data?.name?.toLowerCase().includes(filterOptions.namePattern.toLowerCase());
            
            return hasValidName && isNode && passesCustomFilters && passesNameFilter;
        });
        
        return filteredElements.sort((a, b) => {
            const aName = a?.data?.name || '';
            const bName = b?.data?.name || '';
            return aName.localeCompare(bName, undefined, { sensitivity: 'base' });
        });
    }, [elements, filterOptions?.excludeGroups, filterOptions?.namePattern]);

    return (
        <ThemeProvider theme={themeOverrides}>
            <Autocomplete
                disablePortal
                id="searchBar"
                onChange={(event, newValue) => handleSearch(newValue)}
                options={sortedElements}
                getOptionLabel={(option) => option.data.name}
                sx={{ minWidth: 120, width: 450}}
                renderOption={renderOption}
                renderInput={renderInput}
            />
        </ThemeProvider>
    );
});

AppBarSearch.displayName = 'AppBarSearch';